import { useMemo, useState } from "react";
import dayjs from "dayjs";
import { HiOutlineExternalLink } from "react-icons/hi";
import { ArrowDown, ArrowUp, Loader2 } from "lucide-react";
import {
  ILabReportInterpretationEMRDocuments,
  ILabReportInterpretationListResponseData,
} from "@/api_framework/api_modals/LabReportInterpretation";
import TableSkeletonComponent from "@/app/components/ui/LoaderComponent";
import TableComponent, { type TableColumn } from "@/app/components/ui/Reusable/TableComponent";
import { useLabReportInterpretationEMRCreateAPI } from "@/app/hooks/api_hooks/LabReportInterpretation/useLabReportInterpretationEMRCreateAPI";
import { useLabReportInterpretationPendingRequisitionsAPI } from "@/app/hooks/api_hooks/LabReportInterpretation/useLabReportInterpretationPendingRequisitionsAPI";
import { useModalActions } from "@/app/hooks/ui_hooks/useModalActions";
import { useRouter } from "@/app/hooks/useRouter";
import { useUtils } from "@/app/hooks/useUtils";
import LabReportInterpretationEMRModal from "../LabReportInterpretationEMRModal";
import RequisitionSelectionModal from "./RequisitionSelectionModal";

const TAT_DATE_TIME_FORMAT = "DD MMM YY, hh:mm A";

const INTERPRETATION_STATUS_CLASSES: Record<string, string> = {
  interpretation_pending: "bg-[#FFF8E6] text-[#756100] ring-[#856E00]",
  sample_under_process: "bg-[#EAF2FF] text-[#0467FF] ring-[#0467FF]",
  submitted: "bg-[#F4FFF4] text-[#00770c] ring-[#00860D]",
};

const LINK_CLASS = "inline-flex items-center gap-1 text-[#0467ff] font-medium hover:underline whitespace-nowrap";

const ACTION_COLUMN_CLASS = "sticky right-0 z-[5] bg-white shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.2)]";

const ClampedText = ({ text }: { text?: string | null }) =>
  text ? (
    <p title={text} className="line-clamp-3 whitespace-normal break-words">
      {text}
    </p>
  ) : (
    <>-</>
  );

interface ILabReportInterpretationTableProps {
  rows: ILabReportInterpretationListResponseData[];
  loading: boolean;
  error: boolean;
  onRefresh: () => void;
}

const LabReportInterpretationTable = ({ rows, loading, error, onRefresh }: ILabReportInterpretationTableProps) => {
  const { formatStatus } = useUtils();
  const { getRouteKey } = useRouter();
  const [startingRowId, setStartingRowId] = useState<string | null>(null);
  const [testDateSort, setTestDateSort] = useState<"asc" | "desc">("asc");
  const sortedRows = useMemo(
    () =>
      [...rows].sort((a, b) => {
        const diff = dayjs(a?.test_date).valueOf() - dayjs(b?.test_date).valueOf();
        return testDateSort === "asc" ? diff : -diff;
      }),
    [rows, testDateSort],
  );
  const { execute: createEMR } = useLabReportInterpretationEMRCreateAPI();
  const [isSubmittingRequisitions, setIsSubmittingRequisitions] = useState(false);
  const { execute: fetchPendingRequisitions } = useLabReportInterpretationPendingRequisitionsAPI();
  const {
    setVisible: openRequisitionModal,
    setInvisible: closeRequisitionModal,
    setModalData: setRequisitionModalData,
  } = useModalActions("LAB_REPORT_INTERPRETATION_REQUISITIONS_MODAL");
  const { setVisible: openEMRModal, setModalData: setEMRModalData } = useModalActions(
    "LAB_REPORT_INTERPRETATION_EMR_MODAL",
  );

  const showEMR = (emrId: string, documents?: ILabReportInterpretationEMRDocuments) => {
    setEMRModalData({ emrId, documents });
    openEMRModal();
  };

  const handleFillInterpretation = async (row: ILabReportInterpretationListResponseData) => {
    if (row.draft_lab_interpretation) {
      showEMR(row.emr_id);
      return;
    }
    setStartingRowId(row.id);
    const requisitions = await fetchPendingRequisitions(row.lab_details?.requisition_id, row.test_date);
    if (requisitions?.length === 1) await handleConfirmRequisitions(row, [requisitions[0].requisition_id]);
    setStartingRowId(null);
    if (!requisitions || requisitions.length === 1) return;
    setRequisitionModalData({ row, requisitions });
    openRequisitionModal();
  };

  const handleConfirmRequisitions = async (row: ILabReportInterpretationListResponseData, requisitionIds: string[]) => {
    setIsSubmittingRequisitions(true);
    const createdEMR = await createEMR({
      customer_id: row?.client?.user_id,
      pet_id: row?.pet?.pet_id,
      clinic_id: row?.clinic_id,
      lab_requisition_ids: requisitionIds,
    });
    setIsSubmittingRequisitions(false);
    if (!createdEMR) return;
    closeRequisitionModal();
    showEMR(createdEMR.emr_id, createdEMR);
    onRefresh();
  };

  const getCustomerProfileUrl = (row: ILabReportInterpretationListResponseData) =>
    `${getRouteKey("LAB_INTERPRETATION_TAB", "url")}/${row?.client?.user_id}/${row?.pet?.pet_id}`;

  const renderAction = (row: ILabReportInterpretationListResponseData) => {
    if (row?.status === "interpretation_pending") {
      return (
        <button
          disabled={startingRowId === row.id}
          onClick={() => handleFillInterpretation(row)}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-black text-white text-xs font-medium whitespace-nowrap hover:opacity-90 disabled:opacity-70"
        >
          {startingRowId === row.id && <Loader2 className="w-3.5 h-3.5 motion-safe:animate-spin" aria-hidden="true" />}
          {startingRowId === row.id
            ? "Opening..."
            : row.draft_lab_interpretation
              ? "Update Interpretation"
              : "Fill Interpretation"}
        </button>
      );
    }
    if (row?.status === "submitted") {
      return (
        <div className="flex items-center gap-2">
          <button
            disabled={!row.interpretation_details?.emr_pdf_link}
            onClick={() => window.open(row.interpretation_details.emr_pdf_link, "_blank", "noopener,noreferrer")}
            className="px-3 py-2 rounded-lg bg-black text-white text-xs font-medium whitespace-nowrap hover:opacity-90 disabled:opacity-50"
          >
            View Interpretation
          </button>
          <button
            onClick={() => showEMR(row.emr_id)}
            className="px-3 py-2 rounded-lg border border-gray-300 bg-white text-gray-900 text-xs font-medium hover:bg-gray-50 disabled:opacity-50"
          >
            Edit
          </button>
        </div>
      );
    }
    return <span className="text-gray-400">-</span>;
  };

  const columns: TableColumn<ILabReportInterpretationListResponseData>[] = [
    {
      id: "client",
      header: "Client Details",
      minWidth: 160,
      cell: ({ row }) => (
        <a
          href={getCustomerProfileUrl(row)}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#0B6EFF] font-medium hover:underline"
        >
          {row?.client?.name || "-"}
        </a>
      ),
    },
    {
      id: "pet",
      header: "Pet Details",
      minWidth: 170,
      cell: ({ row }) => (
        <div>
          <p className="font-medium">{row?.pet?.name || "-"}</p>
          <p className="text-[#6C727F] mt-0.5">
            {[row?.pet?.species, row?.pet?.breed].filter(Boolean).join(" · ") || "-"}
          </p>
        </div>
      ),
    },
    {
      id: "test_name",
      header: "Test Name",
      minWidth: 220,
      maxWidth: 280,
      cell: ({ row }) => <ClampedText text={row?.lab_report_name} />,
    },
    {
      id: "test_date",
      header: (
        <button
          type="button"
          onClick={() => setTestDateSort((prev) => (prev === "asc" ? "desc" : "asc"))}
          title={testDateSort === "asc" ? "Oldest first" : "Newest first"}
          className="inline-flex items-center gap-1 hover:opacity-75"
        >
          Test Date
          {testDateSort === "asc" ? (
            <ArrowUp className="w-3.5 h-3.5" aria-hidden="true" />
          ) : (
            <ArrowDown className="w-3.5 h-3.5" aria-hidden="true" />
          )}
        </button>
      ),
      minWidth: 130,
      cell: ({ row }) => (
        <span className="whitespace-nowrap">
          {row?.test_date ? dayjs(row.test_date).format(TAT_DATE_TIME_FORMAT) : "-"}
        </span>
      ),
    },
    {
      id: "documents",
      header: "View Documents",
      minWidth: 140,
      cell: ({ row }) =>
        row?.emr_details?.emr_pdf_link || row?.lab_details?.report_urls?.length ? (
          <div className="flex flex-col gap-1">
            {row?.emr_details?.emr_pdf_link && (
              <a href={row.emr_details.emr_pdf_link} target="_blank" rel="noopener noreferrer" className={LINK_CLASS}>
                View EMR <HiOutlineExternalLink />
              </a>
            )}
            {row?.lab_details?.report_urls?.map((url, index) => (
              <a key={url} href={url} target="_blank" rel="noopener noreferrer" className={LINK_CLASS}>
                {row.lab_details.report_urls.length > 1 ? `View Report ${index + 1}` : "View Report"}{" "}
                <HiOutlineExternalLink />
              </a>
            ))}
          </div>
        ) : (
          "-"
        ),
    },
    {
      id: "lab_report_tat",
      header: "Expected Timeline",
      minWidth: 200,
      filter: {
        options: [
          { label: "All", value: "all" },
          { label: "Delayed", value: "delayed" },
          { label: "Not Delayed", value: "not_delayed" },
        ],
        allValue: "all",
        buttonClassName: "!text-inherit !font-medium",
        filterFn: (row, value) => !!row?.is_delayed === (value === "delayed"),
      },
      cell: ({ row }) => (
        <div>
          <p className="whitespace-nowrap">
            {row?.lab_report_tat ? dayjs(row.lab_report_tat).format(TAT_DATE_TIME_FORMAT) : "-"}
          </p>
          {row?.is_delayed && (
            <span className="inline-block mt-1.5 px-2 py-0.5 rounded-full bg-[#FFEDED] text-[#ED0101] text-[11px] font-medium">
              Delayed
            </span>
          )}
        </div>
      ),
    },
    {
      id: "doctor",
      header: "Doctor",
      minWidth: 160,
      cell: ({ row }) => (
        <div>
          <p>{row?.assigned_doctor?.staff_name || "-"}</p>
          {row?.is_reassigned && (
            <span className="inline-block mt-1.5 px-2 py-0.5 rounded-full bg-[#FFF4E5] text-[#B45309] text-[11px] font-medium">
              Reassigned
            </span>
          )}
        </div>
      ),
    },
    {
      id: "status",
      header: "Status",
      minWidth: 150,
      cell: ({ row }) =>
        row?.status ? (
          <span
            className={`inline-block px-3 py-1 rounded-full font-medium text-center whitespace-pre ring-1 ring-inset ring-opacity-20 ${INTERPRETATION_STATUS_CLASSES[row.status] ?? "bg-gray-100 text-gray-700 ring-gray-400"}`}
          >
            {formatStatus(row.status).replace(/ (?=\S+$)/, "\n")}
          </span>
        ) : (
          "-"
        ),
    },
    {
      id: "lab_partner",
      header: "Lab Partner",
      minWidth: 200,
      maxWidth: 260,
      cell: ({ row }) => <ClampedText text={row?.lab_partner_name} />,
    },
    {
      id: "clinic",
      header: "Clinic",
      minWidth: 180,
      maxWidth: 240,
      cell: ({ row }) => <ClampedText text={row?.clinic_name} />,
    },
    {
      id: "action",
      header: "Action",
      minWidth: 230,
      className: ACTION_COLUMN_CLASS,
      headerClassName: "sticky right-0 z-[6] shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.2)]",
      cell: ({ row }) => renderAction(row),
    },
  ];

  return (
    <>
      <TableComponent<ILabReportInterpretationListResponseData>
        columns={columns}
        data={sortedRows}
        getRowId={(row) => row.id}
        loading={loading}
        loadingState={
          <TableSkeletonComponent
            columnLength={columns.length}
            bodyLength={columns.length - 1}
            rowLength={8}
            containerClassName="w-full mt-0 h-auto overflow-hidden rounded-2xl"
          />
        }
        emptyState={error ? "Unable to load interpretations" : undefined}
        containerClassName="border border-gray-200/80 shadow-sm rounded-2xl mr-0 bg-white"
        scrollContainerClassName="overflow-x-auto max-h-[calc(100vh-280px)]"
        minTableWidthClassName="min-w-max"
        tableClassName="min-w-full"
        stickyHeader
        wrapCells
      />
      <RequisitionSelectionModal isSubmitting={isSubmittingRequisitions} onConfirm={handleConfirmRequisitions} />
      <LabReportInterpretationEMRModal onSaved={onRefresh} />
    </>
  );
};

export default LabReportInterpretationTable;
